import { useMediaQuery } from "@mui/material";
import Cookies from "js-cookie";
import { isEmpty } from "lodash";
import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { sendTrackingEvent } from "../../helpers/tracking_management";
import {
  fetchCompanyPageAds,
  getCompany,
  getCompanyAffiliates,
  getCompanyJobs,
  getDiscoverCompanies,
  updateSelectedCompany,
} from "../../redux/actions/company_action";
import { getSpecialisation } from "../../redux/actions/job_action";
import SharedToast from "../shared/SharedToast/SharedToast";
import CompanyAddress from "./CompanyAddress/CompanyAddress";
import CompanyAffiliates from "./CompanyAffiliates/CompanyAffiliates";
import CompanyBenefits from "./CompanyBenefits/CompanyBenefits";
import CompanyDescription from "./CompanyDescription/CompanyDescription";
import CompanyExtras from "./CompanyExtras/CompanyExtras";
import CompanyJobs from "./CompanyJobs/CompanyJobs";
import CompanyNavigation from "./CompanyNavigation/CompanyNavigation";
import CompanyPictures from "./CompanyPictures/CompanyPictures";
import CompanySocialMedia from "./CompanySocialMedia/CompanySocialMedia";
import CompanyVideos from "./CompanyVideos/CompanyVideos";
import DiscoverCompany from "./DiscoverCompany/DiscoverCompany";
import {
  AdImage,
  AdRail,
  AdRailStack,
  AdSlot,
  BottomAdContainer,
  BottomAdSlot,
  ContentLayout,
  DividerStyled,
  InlineAdBlock,
  InlineAdColumn,
  InlineAdRow,
  InlineAdRowItem,
  InlineAdSlot,
  InlineAdSlotBottomRail,
  InlineAdSlotRightRail,
  InnerWrapper,
  MainColumn,
  OuterWrapper,
} from "./styles";

const getAdImageUrl = (adBox) => adBox?.adImage?.data?.attributes?.url || "";
const getAdLink = (adBox) => adBox?.customLink?.trim() || "";

function CompanyAdImage({ adBox, alt = "Partner highlight" }) {
  const imageUrl = getAdImageUrl(adBox);
  const link = getAdLink(adBox);

  if (!imageUrl) return null;

  const image = <AdImage src={imageUrl} alt={alt} />;

  if (!link) return image;

  return (
    <a
      href={link}
      target="_blank"
      rel="noopener noreferrer"
      style={{ display: "block", width: "100%" }}
    >
      {image}
    </a>
  );
}

function CompanyWrapper(props) {
  const { company, staticCompanyJobs } = props || {};

  const dispatch = useDispatch();
  const selectedCompany = useSelector(
    (state) => state?.companies?.selectedCompany
  );

  const isDesktop = useMediaQuery("(min-width: 1280px)");
  const isTablet = useMediaQuery("(max-width: 1280px)");
  const isMobile = useMediaQuery("(max-width: 640px)");

  const [currentTab, setCurrentTab] = useState(1);
  const [companyPageAds, setCompanyPageAds] = useState({});

  const companyId = company?.id;

  const discoverCompaniesParams = {
    companyId: companyId,
    industryId: company?.industryId,
    // companySize: company?.sizeId,
  };

  const companySocial = company?.profile?.socialMediaLinks || [];

  const companySocialExist = companySocial?.length > 0;

  const companyPictures =
    {
      id: company?.id,
      name: company?.name,
      images: company?.profile?.images,
    } || {};

  const companyVideos =
    {
      name: company?.name,
      videos: company?.profile?.videos,
    } || {};

  const companyVideoExist = companyVideos?.videos?.length > 0;

  const companyExtras = company?.profile?.extras || [];

  const companyExtrasExist = companyExtras?.length > 0;

  const companyAddress =
    {
      address: company?.profile?.address,
      latitude: company?.profile?.latitude,
      longitude: company?.profile?.longitude,
    } || {};

  const companyAffiliates =
    {
      name: company?.name,
      id: company?.id,
      affiliates: company?.affiliatedCompany ?? company?.companyAffiliation,
    } || {};

  const companyAffiliatesExist =
    companyAffiliates?.affiliates?.length > 0 ||
    !Array.isArray(companyAffiliates?.affiliates);

  const companyDetail = {
    id: company?.id,
    name: company?.name,
    createdAt: company?.createdAt,
    registrationNumber: company?.registrationNumber,
    location: company?.location,
    industry: company?.industry,
    industryId: company?.industryId,
    jobCount: company?.jobCount,
    videoCount: company?.profile?.videos?.length,
    benefitsCount: company?.benefits?.length,
    sizeId: company?.sizeId,
    logo: company?.logo,
    slug: company?.slug,
    bookmark: company?.bookmark,
    category: company?.category,
    affiliatesExist: companyAffiliatesExist,
    extrasExist: companyExtrasExist,
    recruitmentFirm: company?.recruitmentFirm,
    verified: company?.verified,
    companyApplicationEnabled:
      selectedCompany?.id === company?.id
        ? selectedCompany?.companyApplicationEnabled
        : company?.companyApplicationEnabled,
    companyApplicationSubmitted:
      selectedCompany?.id === company?.id
        ? selectedCompany?.companyApplicationSubmitted
        : company?.companyApplicationSubmitted,
    showAds: company?.showAds,
  };

  const discoverCompanies = useSelector(
    (state) => state.companies.cpDiscoverMoreCompanies
  );

  const filteredDiscoverCompanies = discoverCompanies?.filter(
    (company) => company?.company?.id !== companyDetail?.id
  );

  const companyAffliates = useSelector(
    (state) => state.companies.companyAffiliates.company
  );

  useEffect(() => {
    dispatch(updateSelectedCompany(company));
    dispatch(getCompany({ companyId: companyId }));
    dispatch(getDiscoverCompanies(discoverCompaniesParams));
    dispatch(getCompanyAffiliates({ companyId: companyAffiliates?.id }));

    if (company?.jobCount > 0) {
      dispatch(getSpecialisation());
      dispatch(getCompanyJobs({ companyId: companyId }));
    }

    sendTrackingEvent({
      event: "CE_view-company",
      "company-id": company?.id ?? null,
    });

    setTimeout(() => {
      Cookies.set("click-source", null);
    }, 30000);
  }, [companyId, dispatch, company]);

  useEffect(() => {
    let cancelled = false;

    async function loadCompanyPageAds() {
      const ads = await dispatch(fetchCompanyPageAds());
      if (!cancelled) {
        setCompanyPageAds(ads || {});
      }
    }

    loadCompanyPageAds();

    return () => {
      cancelled = true;
    };
  }, [dispatch]);

  const companyAffiliatesFromRedux = useMemo(() => {
    return (
      companyAffliates?.affiliatedCompany ??
      companyAffliates?.companyAffiliation
    );
  }, [companyAffliates]);

  const filteredCompany = Array.isArray(companyAffiliatesFromRedux)
    ? companyAffiliatesFromRedux?.filter(
      (company) => company?.affiliatedCompany?.hide === false
    )
    : companyAffiliatesFromRedux;

  const showAds = company?.showAds;

  const middleAd = companyPageAds?.middleAd;
  const footerAd = companyPageAds?.footerAd;
  const rightTopAd = companyPageAds?.rightTopAd;
  const rightBottomAd = companyPageAds?.rightBottomAd;

  const middleAdUrl = getAdImageUrl(middleAd);
  const footerAdUrl = getAdImageUrl(footerAd);
  const rightTopAdUrl = getAdImageUrl(rightTopAd);
  const rightBottomAdUrl = getAdImageUrl(rightBottomAd);

  const showRightRail =
    showAds && isDesktop && (rightTopAdUrl || rightBottomAdUrl);

  return (
    <OuterWrapper>
      <SharedToast />
      {/* Company Navigation */}
      <CompanyNavigation
        companyDetail={companyDetail}
        currentTab={currentTab}
        setCurrentTab={setCurrentTab}
        companySocialExist={companySocialExist}
      />

      {/* Company Pictures */}
      {companyPictures?.images?.length > 0 && (
        <>
          <CompanyPictures
            companyDetail={companyPictures}
            setCurrentTab={setCurrentTab}
          />
        </>
      )}

      {/* Our Story + Ad Rail (desktop only) */}
      <ContentLayout>
        <MainColumn>
          <InnerWrapper>
            <CompanyDescription
              companyDetail={company?.profile?.descriptions}
              companySocial={companySocial}
              setCurrentTab={setCurrentTab}
            />
          </InnerWrapper>
          <DividerStyled />

          {isMobile && showAds && (rightTopAdUrl || middleAdUrl) ? (
            <InlineAdColumn>
              {rightTopAdUrl ? (
                <InlineAdSlotRightRail>
                  <CompanyAdImage adBox={rightTopAd} />
                </InlineAdSlotRightRail>
              ) : null}
              {middleAdUrl ? (
                <InlineAdSlot>
                  <CompanyAdImage adBox={middleAd} />
                </InlineAdSlot>
              ) : null}
            </InlineAdColumn>
          ) : null}

          {!isMobile && showAds && middleAdUrl ? (
            <>
              <InlineAdBlock>
                <InlineAdSlot>
                  <CompanyAdImage adBox={middleAd} />
                </InlineAdSlot>
              </InlineAdBlock>

              <DividerStyled />
            </>
          ) : null}

          {isMobile && showAds && (rightTopAdUrl || middleAdUrl) ? (
            <DividerStyled />
          ) : null}

          {/* Company Media Videos */}
          {companyVideoExist ? (
            <>
              <InnerWrapper>
                <CompanyVideos
                  companyDetail={companyVideos}
                  setCurrentTab={setCurrentTab}
                />
              </InnerWrapper>
              <DividerStyled />
            </>
          ) : null}

          {/* Company Benefits */}
          {company?.benefits?.length > 0 ? (
            <>
              <CompanyBenefits
                companyDetail={company?.benefits}
                setCurrentTab={setCurrentTab}
              />
              <DividerStyled />
            </>
          ) : null}

          {isMobile && showAds && rightBottomAdUrl ? (
            <InlineAdBlock>
              <InlineAdSlotBottomRail>
                <CompanyAdImage adBox={rightBottomAd} />
              </InlineAdSlotBottomRail>
            </InlineAdBlock>
          ) : null}

          {isTablet &&
            !isMobile &&
            showAds &&
            (rightTopAdUrl || rightBottomAdUrl) ? (
            <InlineAdRow>
              {rightTopAdUrl ? (
                <InlineAdRowItem $aspect={300 / 250}>
                  <CompanyAdImage adBox={rightTopAd} />
                </InlineAdRowItem>
              ) : null}
              {rightBottomAdUrl ? (
                <InlineAdRowItem $aspect={300 / 600}>
                  <CompanyAdImage adBox={rightBottomAd} />
                </InlineAdRowItem>
              ) : null}
            </InlineAdRow>
          ) : null}

          {/* Company Extras */}
          {companyExtrasExist ? (
            <>
              <CompanyExtras companyDetail={companyExtras} />
              <DividerStyled />
            </>
          ) : null}

          {/* Company Social Media */}
          {companySocialExist ? (
            <>
              <CompanySocialMedia
                companyDetail={companySocial}
                setCurrentTab={setCurrentTab}
              />
              <DividerStyled />
            </>
          ) : null}

          {/* Company Address */}
          <CompanyAddress companyDetail={companyAddress} />
          <DividerStyled />

          {/* Company Affiliates */}
          {companyAffiliatesExist && !isEmpty(filteredCompany) && (
            <>
              <CompanyAffiliates companyAffiliates={filteredCompany} />
              <DividerStyled />
            </>
          )}

          {/* Company Job List */}
          {company?.jobCount > 0 || staticCompanyJobs?.length > 0 ? (
            <>
              <CompanyJobs
                setCurrentTab={setCurrentTab}
                staticCompanyJobs={staticCompanyJobs}
                hasAdRail={showRightRail}
              />
              <DividerStyled />
            </>
          ) : null}

          {/* Discover Company */}
          {filteredDiscoverCompanies?.length > 0 && (
            <>
              <DiscoverCompany companyDetail={companyDetail} />
            </>
          )}
        </MainColumn>

        {showRightRail && (
          <AdRail>
            <AdRailStack>
              {rightTopAdUrl ? (
                <AdSlot>
                  <CompanyAdImage adBox={rightTopAd} />
                </AdSlot>
              ) : null}
              {rightBottomAdUrl ? (
                <AdSlot>
                  <CompanyAdImage adBox={rightBottomAd} />
                </AdSlot>
              ) : null}
            </AdRailStack>
          </AdRail>
        )}
      </ContentLayout>

      {showAds && footerAdUrl ? (
        <BottomAdContainer>
          <BottomAdSlot>
            <CompanyAdImage adBox={footerAd} />
          </BottomAdSlot>
        </BottomAdContainer>
      ) : null}
    </OuterWrapper>
  );
}

export default CompanyWrapper;
